import { DeviceStatusResponse, DeviceStatusType } from '@trackman/baseball-shared-components';
import { initI18n } from 'i18n';
import { ISessionMode } from 'interfaces/session/ISessionMode';
import { IApiSessionMetadataUpdated } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdated';
import { ISaltUpdate, ISoftwareUpdate } from 'interfaces/tagging/ApiModels/IUpdateMessage';
import { configure } from 'mobx';
import React from 'react';
import { createRoot } from 'react-dom/client';
import 'reflect-metadata';
import { AxiosServiceConfig } from 'services/AxiosServiceConfig';
import { AxiosService, AxiosServiceFactory } from 'services/AxiosServiceFactory';
import { ConfigurationService } from 'services/ConfigurationService';
import { FeatureConfigService } from 'services/FeatureConfigService';
import RulesetService from 'services/RulesetService';
import { GameReviewService } from 'services/data-services/GameReviewService';
import { PitchComService } from 'services/data-services/PitchComService';
import { PlayerService } from 'services/data-services/PlayerService';
import { SessionManagerService } from 'services/data-services/SessionManagerService';
import { SessionManagerServiceExternal } from 'services/data-services/SessionManagerServiceExternal';
import TaggingSignalRService from 'services/data-services/TaggingSignalRService';
import { VideoUploadService } from 'services/data-services/VideoUploadService';
import { ContextStore } from 'stores/ContextStore';
import { ContextualDataStore } from 'stores/ContextualDataStore';
import { GameReviewStore } from 'stores/GameReviewStore';
import { LineupStore } from 'stores/LineupStore';
import { MetricStore } from 'stores/MetricStore';
import NoteStore from 'stores/NoteStore';
import { NotificationStore } from 'stores/NotificationStore';
import { SessionStore } from 'stores/SessionStore';
import { ExtraInningRulesStore } from 'stores/Tagging/ExtraInningRulesStore';
import { TaggingStore } from 'stores/Tagging/TaggingStore';
import { TeamStore } from 'stores/TeamStore';
import { TrackingElementsStore } from 'stores/TrackingElementsStore';
import { UiStore } from 'stores/UiStore';
import VideoUploaderStateStore from 'stores/VideoUploaderStateStore';
import WizardStore from 'stores/WizardStore';
import App from './App';
import { ServiceTypes } from './constants/ServiceTypes';
import './index.css';
import * as serviceWorker from './serviceWorker';
import { BroadcastConsumerService } from './services/data-services/BroadcastConsumerService';
import { ContextualService } from './services/data-services/ContextualService';
import { LogService } from './services/data-services/LogService';
import { ScheduleService } from './services/data-services/ScheduleService';
import { TaggingService } from './services/data-services/TaggingService';
import { HotkeyService } from './services/helper-services/HotkeyService';
import { IntervalWorkerService } from './services/helper-services/IntervalWorkerService';
import { TrackingContextProvider } from './services/helper-services/TrackingContextProvider';
import { container } from './services/ioc/ioc';
import { FieldCalibrationStore } from './stores/FieldCalibrationStore';
import { BpTaggingStore } from './stores/Tagging/BpTaggingStore';

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
configure({ enforceActions: 'never' });

function reactRender() {
  const root = createRoot(document.getElementById('root')!);

  root.render(<App />);
}

async function renderApp() {
  if (window?.location?.origin?.includes('https://')) {
    window.location.href = window.location.href.replace('https://', 'http://');
    return;
  }

  const intervalWorkerService = new IntervalWorkerService();
  const trackingContextProvider = new TrackingContextProvider();
  const configurationService = new ConfigurationService();
  await configurationService.fetchConfigs();
  const serviceFactory = new AxiosServiceFactory(configurationService);
  const hotkeyService = new HotkeyService();
  const rulesetService = new RulesetService();

  const notificationStore = new NotificationStore();

  const apiService = await serviceFactory.createInstance(ServiceTypes.ApiService);
  const contextualServiceAxiosInstance = await serviceFactory.createInstance(ServiceTypes.ContextualService);
  const sessionManagerServiceExternalInstance = await serviceFactory.createInstance(ServiceTypes.SessionManagerServiceExternal);

  const logService = new LogService(apiService, trackingContextProvider);
  const contextStore = new ContextStore();
  const contextualService = new ContextualService(contextualServiceAxiosInstance, notificationStore);
  const sessionManagerServiceExternal = new SessionManagerServiceExternal(sessionManagerServiceExternalInstance, contextStore);
  const sessionManagerService = new SessionManagerService(apiService);
  const scheduleService = new ScheduleService(apiService);
  const contextualDataStore = new ContextualDataStore(contextualService, scheduleService, logService, contextStore, notificationStore);
  const uiStore = new UiStore();

  const sessionStore = new SessionStore(
    sessionManagerService,
    sessionManagerServiceExternal,
    contextualDataStore,
    logService,
    intervalWorkerService,
    contextualService,
    notificationStore,
    uiStore
  );
  const featureConfigService = new FeatureConfigService(apiService);
  const taggingService = new TaggingService(apiService, contextStore, logService);
  const videoUploadService = new VideoUploadService(apiService);
  const teamStore = new TeamStore(contextualService, logService, notificationStore);
  const lineupStore = new LineupStore(logService, contextualDataStore, featureConfigService);
  const extraInningRulesStore = new ExtraInningRulesStore();
  const playerService = new PlayerService(apiService);
  const noteStore = new NoteStore();

  const taggingStore = new TaggingStore(
    taggingService,
    sessionStore,
    contextualService,
    sessionManagerService,
    logService,
    hotkeyService,
    contextStore,
    extraInningRulesStore,
    playerService,
    noteStore,
    featureConfigService,
    notificationStore
  );
  const videoUploaderStateStore = new VideoUploaderStateStore();
  const trackingElementsStore = new TrackingElementsStore(sessionStore);

  const broadcastService = new BroadcastConsumerService(
    configurationService.getConfigs().dataBroadcasterUrl,
    sessionStore,
    logService,
    videoUploaderStateStore,
    trackingElementsStore
  );
  const gameReviewService = new GameReviewService(apiService);
  const gameReviewStore = new GameReviewStore(
    gameReviewService,
    taggingService,
    sessionManagerService,
    sessionManagerServiceExternal,
    logService,
    contextualService,
    notificationStore
  );
  const bpTaggingStore = new BpTaggingStore(
    taggingService,
    lineupStore,
    sessionStore,
    sessionManagerService,
    logService,
    hotkeyService,
    contextStore,
    notificationStore
  );
  const metricStore = new MetricStore(featureConfigService);
  const fieldCalibrationStore = new FieldCalibrationStore();
  const wizardStore = new WizardStore();

  const taggingSignalRService = new TaggingSignalRService(
    configurationService.getWindowOrigin(true) + '/tagginghub',
    uiStore,
    logService,
    trackingElementsStore
  );
  const pitchComService = new PitchComService(apiService, configurationService);

  container.bind<AxiosService>(ServiceTypes.ApiService).toDynamicValue(() => apiService);
  container.bind<ConfigurationService>(ServiceTypes.ConfigurationService).to(ConfigurationService).inSingletonScope();
  container.bind<TaggingStore>(ServiceTypes.TaggingStore).toDynamicValue(() => taggingStore);
  container.bind<RulesetService>(ServiceTypes.RulesetService).toDynamicValue(() => rulesetService);
  container.bind<ContextualService>(ServiceTypes.ContextualService).toDynamicValue(() => contextualService);
  container.bind<ScheduleService>(ServiceTypes.ScheduleService).toDynamicValue(() => scheduleService);
  container.bind<TeamStore>(ServiceTypes.TeamStore).toDynamicValue(() => teamStore);
  container.bind<LineupStore>(ServiceTypes.LineupStore).toDynamicValue(() => lineupStore);
  container.bind<SessionStore>(ServiceTypes.SessionStore).toDynamicValue(() => sessionStore);
  container
    .bind<SessionManagerServiceExternal>(ServiceTypes.SessionManagerServiceExternal)
    .toDynamicValue(() => sessionManagerServiceExternal);
  container.bind<ContextualDataStore>(ServiceTypes.ContextualDataStore).toDynamicValue(() => contextualDataStore);
  container.bind<BroadcastConsumerService>(ServiceTypes.BroadcastConsumerService).toDynamicValue(() => broadcastService);
  container.bind<TaggingSignalRService>(ServiceTypes.TaggingSignalRService).toDynamicValue(() => taggingSignalRService);
  container.bind<GameReviewStore>(ServiceTypes.GameReviewStore).toDynamicValue(() => gameReviewStore);
  container.bind<GameReviewService>(ServiceTypes.GameReviewService).toDynamicValue(() => gameReviewService);
  container.bind<UiStore>(ServiceTypes.UiStore).toDynamicValue(() => uiStore);
  container.bind<VideoUploaderStateStore>(ServiceTypes.VideoUploaderStateStore).toDynamicValue(() => videoUploaderStateStore);
  container.bind<LogService>(ServiceTypes.LogService).toDynamicValue(() => logService);
  container.bind<HotkeyService>(ServiceTypes.HotkeyService).toDynamicValue(() => hotkeyService);
  container.bind<BpTaggingStore>(ServiceTypes.BpTaggingStore).toDynamicValue(() => bpTaggingStore);
  container.bind<MetricStore>(ServiceTypes.MetricStore).toDynamicValue(() => metricStore);
  container.bind<ContextStore>(ServiceTypes.ContextStore).toDynamicValue(() => contextStore);
  container.bind<TrackingContextProvider>(ServiceTypes.TrackingContextProvider).toDynamicValue(() => trackingContextProvider);
  container.bind<IntervalWorkerService>(ServiceTypes.IntervalWorkerService).toDynamicValue(() => intervalWorkerService);
  container.bind<VideoUploadService>(ServiceTypes.VideoUploadService).toDynamicValue(() => videoUploadService);
  container.bind<FeatureConfigService>(ServiceTypes.FeatureConfigService).toDynamicValue(() => featureConfigService);
  container.bind<FieldCalibrationStore>(ServiceTypes.FieldCalibrationStore).toDynamicValue(() => fieldCalibrationStore);
  container.bind<WizardStore>(ServiceTypes.WizardStore).toDynamicValue(() => wizardStore);
  container.bind<ExtraInningRulesStore>(ServiceTypes.ExtraInningRulesStore).toDynamicValue(() => extraInningRulesStore);
  container.bind<TrackingElementsStore>(ServiceTypes.TrackingElementsStore).toDynamicValue(() => trackingElementsStore);
  container.bind<PitchComService>(ServiceTypes.PitchComService).toDynamicValue(() => pitchComService);
  container.bind<NoteStore>(ServiceTypes.NoteStore).toDynamicValue(() => noteStore);
  container.bind<NotificationStore>(ServiceTypes.NotificationStore).toDynamicValue(() => notificationStore);

  // Add a request and response interceptor
  AxiosServiceConfig.setDefaultDecorators(apiService, logService, trackingContextProvider);
  AxiosServiceConfig.setDefaultDecorators(contextualServiceAxiosInstance, logService, trackingContextProvider);
  AxiosServiceConfig.setDefaultDecorators(sessionManagerServiceExternalInstance, logService, trackingContextProvider);

  await initI18n();
  contextStore.setActiveField(configurationService.getConfigs().activeField);
  contextStore.setLoggedIn(configurationService.getConfigs().isUserLogged);
  const promises = [];
  promises.push(sessionStore.loadSessionData());
  promises.push(contextualDataStore.fetchGameLevels());
  promises.push(contextualDataStore.fetchLeagues());
  promises.push(contextualDataStore.fetchOwningOrganizations());
  if (contextStore.isConnectedPtu) {
    promises.push(featureConfigService.fetchConfig().then(() => {
      metricStore.setIsStrikeZoneDecisionEnabled(featureConfigService.getConfig().isRoboUmpEnabled);
    }));
  }
  await Promise.all(promises);
  lineupStore.setStrikeZone(contextualDataStore.defaultStrikeZone);
  lineupStore.setIsVideoCaptureEnabled(featureConfigService.getConfig().isUploadVideoEnabled);
  lineupStore.setIsPlayerTrackingEnabled(featureConfigService.getConfig().isPlayerTrackingEnabled);

  broadcastService.subscribeSessionDataChanged(
    (mode: ISessionMode) => sessionStore.onSessionModeChanged(mode),
    (metadata: IApiSessionMetadataUpdated) => {
      sessionStore.onSessionMetadataChanged(metadata);
      metricStore.setIsStrikeZoneDecisionEnabled(featureConfigService.getConfig().isRoboUmpEnabled);
    }
  );
  broadcastService.subscribeActiveFieldChanged(contextStore.setActiveField);
  broadcastService.subscribeLoggedInChanged(contextStore.setLoggedIn);

  broadcastService.subscribeOnDeviceStatusChanged((deviceStatusResponse: DeviceStatusResponse) => {
    trackingElementsStore.setStatuses(deviceStatusResponse.statuses);
    contextStore.setIsConnectedPtu(
      deviceStatusResponse.statuses.find((status) => status.deviceType === 'Ptu')?.status !== DeviceStatusType.Disconnected ? true : false
    );
  });

  broadcastService.onUploaderEstimatesChanged();
  broadcastService.subscribeSaltAndServicesDeploymentStatusChanged((message: ISaltUpdate) => {
    switch (message.status) {
      case 'pending':
        notificationStore.addNotification({
          id: 'salt-software-update-notification',
          message: 'System update available - laptop will need to be online to complete',
          variant: 'software-update',
          removable: false,
        });
        break;
      case 'completed':
        notificationStore.removeNotification('salt-software-update-notification');
        uiStore.setIsSystemUpdateCompleted(true);
    }
  });

  broadcastService.subscribePtuDeploymentStatusChanged((message: ISoftwareUpdate) => {
    switch (message.status) {
      case 'pending':
        trackingElementsStore.setUpdating(true);
        break;
      case 'completed':
        uiStore.setIsPTUUpdateCompleted(true);
        trackingElementsStore.setUpdating(false);
    }
  });

  taggingSignalRService.start();
  broadcastService.start();

  contextualDataStore.fetchScheduledGames();
  sessionStore.setupUpcomingGameCheck();
  logService.logInfo('TaggingApp Open');
  reactRender();
}

renderApp();
